import { PromptRuleForm } from '@/components/prompts/PromptRuleForm';
import {
  Button,
  ConfirmDialog,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyTableState,
  Switch,
  toast,
  useApiClient,
} from '@startupquickstart/frontend';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { CSS } from '@dnd-kit/utilities';
import { ChevronDown, ChevronUp, ClipboardCopy, GripVertical, Import, List, Pencil, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface PromptRule {
  id: string;
  body: string;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

interface PromptRulesListProps {
  /** Prompt ID used to build API endpoint paths */
  promptId: string;
  /** Current list of rules, sorted by sortOrder */
  rules: PromptRule[];
}

// ---------------------------------------------------------------------------
// Inline edit cell
// ---------------------------------------------------------------------------

interface InlineEditCellProps {
  value: string;
  onSave: (value: string) => Promise<void>;
  isPending: boolean;
}

/**
 * Renders rule body text that becomes an editable input on click.
 * Saves on blur or Enter key; cancels on Escape.
 */
function InlineEditCell({ value, onSave, isPending }: InlineEditCellProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const handleSave = async () => {
    const trimmed = draft.trim();
    if (!trimmed || trimmed === value) {
      setEditing(false);
      setDraft(value);
      return;
    }
    await onSave(trimmed);
    setEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      setEditing(false);
      setDraft(value);
    }
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={handleSave}
        onKeyDown={handleKeyDown}
        disabled={isPending}
        className="flex-1 min-w-0 text-sm border rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary bg-background"
        aria-label="Edit rule text"
      />
    );
  }

  return (
    <button
      type="button"
      onClick={() => { setEditing(true); setDraft(value); }}
      title="Click to edit"
      className="flex-1 min-w-0 text-left text-sm text-foreground leading-relaxed group/edit flex items-center gap-1.5 cursor-text"
    >
      <span>{value}</span>
      <Pencil className="h-3 w-3 shrink-0 opacity-0 group-hover/edit:opacity-50 transition-opacity" />
    </button>
  );
}

// ---------------------------------------------------------------------------
// Sortable rule row
// ---------------------------------------------------------------------------

interface SortableRuleRowProps {
  rule: PromptRule;
  index: number;
  total: number;
  isUpdating: boolean;
  onToggle: (rule: PromptRule, enabled: boolean) => void;
  onBodySave: (rule: PromptRule, body: string) => Promise<void>;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDelete: () => void;
  isReordering: boolean;
}

/**
 * A single sortable rule row with drag handle, toggle, inline edit, arrows, and delete.
 */
function SortableRuleRow({
  rule, index, total, isUpdating, onToggle, onBodySave,
  onMoveUp, onMoveDown, onDelete, isReordering,
}: SortableRuleRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: rule.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : undefined,
    opacity: isDragging ? 0.8 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-3 px-4 py-3 ${isDragging ? 'bg-muted shadow-lg rounded-lg' : ''}`}
    >
      {/* Drag handle */}
      <button
        type="button"
        className="touch-none shrink-0 cursor-grab active:cursor-grabbing text-muted-foreground/60 hover:text-muted-foreground"
        {...attributes}
        {...listeners}
        aria-label="Drag to reorder"
      >
        <GripVertical className="h-4 w-4" />
      </button>

      {/* Toggle */}
      <Switch
        checked={true}
        onCheckedChange={(checked) => onToggle(rule, checked)}
        disabled={isUpdating}
        aria-label={`Toggle rule ${index + 1}`}
      />

      {/* Editable body */}
      <InlineEditCell
        value={rule.body}
        onSave={(body) => onBodySave(rule, body)}
        isPending={isUpdating}
      />

      {/* Up / Down */}
      <div className="flex items-center gap-0.5">
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          disabled={index === 0 || isReordering}
          onClick={onMoveUp}
          aria-label="Move rule up"
        >
          <ChevronUp className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          disabled={index === total - 1 || isReordering}
          onClick={onMoveDown}
          aria-label="Move rule down"
        >
          <ChevronDown className="h-4 w-4" />
        </Button>
      </div>

      {/* Delete */}
      <Button
        variant="ghost"
        size="icon"
        className="h-7 w-7 text-destructive hover:bg-destructive/10"
        onClick={onDelete}
        aria-label="Delete rule"
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

/**
 * Full rules editor for a prompt.
 * Supports toggling, inline text editing, drag-and-drop reordering,
 * up/down arrow reordering, deletion, and adding new rules.
 * Reordering is optimistic — the UI updates instantly, server syncs in background.
 *
 * @param promptId - The prompt's ID used for API calls.
 * @param rules - Sorted list of current rules from the parent query.
 */
export function PromptRulesList({ promptId, rules }: PromptRulesListProps) {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importJson, setImportJson] = useState('');
  const [importError, setImportError] = useState<string | null>(null);

  // Optimistic local order — used so reorder is instant
  const [optimisticRules, setOptimisticRules] = useState<PromptRule[]>([]);

  // Sync from server when rules prop changes (and we're not mid-drag)
  useEffect(() => {
    setOptimisticRules([...rules].sort((a, b) => a.sortOrder - b.sortOrder));
  }, [rules]);

  const invalidate = useCallback(
    () => queryClient.invalidateQueries({ queryKey: ['Prompt', promptId] }),
    [queryClient, promptId],
  );

  // dnd-kit sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // -------------------------------------------------------------------------
  // Create rule
  // -------------------------------------------------------------------------
  const createMutation = useMutation({
    mutationFn: async (body: string) => {
      const nextOrder = optimisticRules.length > 0 ? optimisticRules[optimisticRules.length - 1].sortOrder + 1 : 1;
      return apiClient.post(`/prompts/${promptId}/rules`, { body, sortOrder: nextOrder });
    },
    onSuccess: () => invalidate(),
    onError: (err: Error) => toast.error(err?.message ?? 'Failed to add rule'),
  });

  // -------------------------------------------------------------------------
  // Toggle enabled / update body
  // -------------------------------------------------------------------------
  const updateMutation = useMutation({
    mutationFn: async ({ ruleId, payload }: { ruleId: string; payload: Record<string, unknown> }) => {
      return apiClient.put(`/prompts/${promptId}/rules/${ruleId}`, payload);
    },
    onSuccess: () => invalidate(),
    onError: (err: Error) => toast.error(err?.message ?? 'Failed to update rule'),
  });

  // -------------------------------------------------------------------------
  // Delete rule (optimistic — remove from UI immediately, server in background)
  // -------------------------------------------------------------------------
  const deleteMutation = useMutation({
    mutationFn: async (ruleId: string) => {
      return apiClient.delete(`/prompts/${promptId}/rules/${ruleId}`);
    },
    onSuccess: () => invalidate(),
    onError: (err: Error) => {
      toast.error(err?.message ?? 'Failed to delete rule');
      invalidate(); // revert optimistic by re-fetching server state
    },
  });

  // -------------------------------------------------------------------------
  // Reorder rules (fires to server, UI already updated optimistically)
  // -------------------------------------------------------------------------
  const reorderMutation = useMutation({
    mutationFn: async (reordered: Array<{ id: string; sortOrder: number }>) => {
      return apiClient.put(`/prompts/${promptId}/rules/reorder`, { rules: reordered });
    },
    onSuccess: () => invalidate(),
    onError: (err: Error) => {
      toast.error(err?.message ?? 'Failed to reorder rules');
      invalidate(); // revert optimistic on failure
    },
  });

  /**
   * Apply a new order optimistically and fire the server request.
   */
  const applyNewOrder = useCallback((newOrder: PromptRule[]) => {
    // Assign fresh sortOrder values based on position
    const withNewOrders = newOrder.map((rule, idx) => ({
      ...rule,
      sortOrder: (idx + 1) * 10,
    }));
    setOptimisticRules(withNewOrders);

    // Fire server request in background
    reorderMutation.mutate(
      withNewOrders.map((r) => ({ id: r.id, sortOrder: r.sortOrder })),
    );
  }, [reorderMutation]);

  /**
   * Handle drag end from dnd-kit.
   */
  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = optimisticRules.findIndex((r) => r.id === active.id);
    const newIndex = optimisticRules.findIndex((r) => r.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = [...optimisticRules];
    const [moved] = reordered.splice(oldIndex, 1);
    reordered.splice(newIndex, 0, moved);

    applyNewOrder(reordered);
  }, [optimisticRules, applyNewOrder]);

  /**
   * Moves a rule up or down via arrow buttons (optimistic).
   */
  const moveRule = useCallback((ruleId: string, direction: 'up' | 'down') => {
    const idx = optimisticRules.findIndex((r) => r.id === ruleId);
    if (idx === -1) return;
    const targetIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (targetIdx < 0 || targetIdx >= optimisticRules.length) return;

    const reordered = [...optimisticRules];
    const [moved] = reordered.splice(idx, 1);
    reordered.splice(targetIdx, 0, moved);

    applyNewOrder(reordered);
  }, [optimisticRules, applyNewOrder]);

  const handleAddRule = async (body: string) => {
    await createMutation.mutateAsync(body);
  };

  const handleToggle = (rule: PromptRule, enabled: boolean) => {
    updateMutation.mutate({ ruleId: rule.id, payload: { enabled } });
  };

  const handleBodySave = async (rule: PromptRule, body: string) => {
    await updateMutation.mutateAsync({ ruleId: rule.id, payload: { body } });
  };

  const handleDeleteConfirm = () => {
    if (!deleteTarget) return;
    // Optimistic: remove from UI immediately
    setOptimisticRules((prev) => prev.filter((r) => r.id !== deleteTarget));
    setDeleteTarget(null);
    // Fire server request in background
    deleteMutation.mutate(deleteTarget);
  };

  /**
   * Copy all rules as a portable JSON array to clipboard.
   */
  const handleCopyRules = useCallback(() => {
    const exportData = optimisticRules.map((r, idx) => ({ body: r.body, sortOrder: idx + 1 }));
    navigator.clipboard.writeText(JSON.stringify(exportData, null, 2));
    toast.success('Rules copied to clipboard');
  }, [optimisticRules]);

  /**
   * Import rules from a JSON array.
   */
  const handleImportRules = useCallback(async () => {
    setImportError(null);
    try {
      const parsed = JSON.parse(importJson);
      if (!Array.isArray(parsed)) {
        setImportError('JSON must be an array of rule objects');
        return;
      }

      const baseOrder = optimisticRules.length > 0 ? optimisticRules[optimisticRules.length - 1].sortOrder + 1 : 1;

      for (let i = 0; i < parsed.length; i++) {
        const item = parsed[i];
        const body = typeof item === 'string' ? item : item?.body;
        if (!body || typeof body !== 'string') {
          setImportError(`Item ${i} is missing a "body" string`);
          return;
        }
        await apiClient.post(`/prompts/${promptId}/rules`, {
          body,
          sortOrder: item.sortOrder ?? baseOrder + i,
        });
      }

      invalidate();
      setShowImportDialog(false);
      setImportJson('');
      toast.success(`Imported ${parsed.length} rules`);
    } catch (err) {
      setImportError(err instanceof SyntaxError ? 'Invalid JSON' : (err as Error).message);
    }
  }, [importJson, optimisticRules, promptId, apiClient, invalidate]);

  const deleteTargetRule = optimisticRules.find((r) => r.id === deleteTarget);

  return (
    <div className="space-y-3">
      {/* Rules list with drag-and-drop */}
      {optimisticRules.length === 0 ? (
        <EmptyTableState
          icon={<List className="h-5 w-5" />}
          title="No rules yet"
          description="Add a rule below to get started"
        />
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          modifiers={[restrictToVerticalAxis]}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={optimisticRules.map((r) => r.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="divide-y rounded-lg border bg-background">
              {optimisticRules.map((rule, idx) => (
                <SortableRuleRow
                  key={rule.id}
                  rule={rule}
                  index={idx}
                  total={optimisticRules.length}
                  isUpdating={updateMutation.isPending}
                  onToggle={handleToggle}
                  onBodySave={handleBodySave}
                  onMoveUp={() => moveRule(rule.id, 'up')}
                  onMoveDown={() => moveRule(rule.id, 'down')}
                  onDelete={() => setDeleteTarget(rule.id)}
                  isReordering={reorderMutation.isPending}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {/* Add rule form with inline copy/import buttons */}
      <PromptRuleForm
        onSubmit={handleAddRule}
        isPending={createMutation.isPending}
        actions={
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={handleCopyRules}
              disabled={optimisticRules.length === 0}
              title="Copy rules as JSON"
              type="button"
            >
              <ClipboardCopy className="h-3.5 w-3.5" />
              Copy Rules
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setShowImportDialog(true); setImportJson(''); setImportError(null); }}
              title="Import rules from JSON"
              type="button"
            >
              <Import className="h-3.5 w-3.5" />
              Import Rules
            </Button>
          </>
        }
      />

      {/* Import dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Import Rules</DialogTitle>
            <DialogDescription>
              Paste a JSON array of rules. Each item should have a &quot;body&quot; field.
              Imported rules are appended to the existing list.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <textarea
              value={importJson}
              onChange={(e) => { setImportJson(e.target.value); setImportError(null); }}
              placeholder={'[\n  { "body": "Rule text here", "sortOrder": 1 },\n  { "body": "Another rule" }\n]'}
              className="w-full h-40 text-sm font-mono border rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary bg-background resize-none"
            />
            {importError && (
              <p className="text-sm text-destructive">{importError}</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowImportDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleImportRules} disabled={!importJson.trim()}>
              <Import className="h-4 w-4" />
              Import
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}
        title="Delete Rule"
        description={deleteTargetRule ? `Delete rule: "${deleteTargetRule.body}"? This cannot be undone.` : 'Delete this rule?'}
        confirmText="Delete"
        variant="destructive"
        loading={deleteMutation.isPending}
        onConfirm={handleDeleteConfirm}
      />
    </div>
  );
}
